<template>
    <div
        v-if="imagens && imagens.length > 0"
        class="az-thumbnails-bar"
        :class="temaClass"
    >
        <div class="az-thumbnails-content">
            <v-btn
                v-if="mostrarSetas"
                :disabled="!setaEsquerdaAtiva"
                icon
                class="az-thumbnails-arrow az-thumbnails-arrow--left"
                @click="scrollThumbs('left')"
                aria-label="Ver imagens anteriores"
                title="Anterior"
            >
                <v-icon>chevron_left</v-icon>
            </v-btn>

            <div
                ref="thumbStrip"
                class="az-thumbnails-wrap"
                :class="{
                    'az-thumbnails-wrap--no-scrollbar': ehPaginacaoDots,
                    'az-thumbnails-wrap--centered': centralizarImagens && !possuiOverflow
                }"
                aria-label="Imagens"
                @scroll="onThumbScroll"
                @wheel.prevent="onThumbWheel"
            >
                <div
                    v-for="(imagem, i) in imagens"
                    :key="`thumb-${i}`"
                    ref="thumbnailItems"
                    class="az-thumbnail"
                    :class="{ 'az-thumbnail--active': possuiValueSelecionado && i === value }"
                    :style="thumbnailItemStyle"
                    @click="selecionarImagem(i)"
                >
                    <div class="az-thumbnail-image-box" :style="thumbnailStyle">
                        <img
                            class="az-thumbnail-img"
                            :src="imagem.src"
                            :alt="imagem.descricaoImagem || `Imagem ${i + 1}`"
                        />
                    </div>

                    <div v-if="showDescription && imagem.descricaoImagem" class="az-thumbnail-description primary--text">
                        {{ imagem.descricaoImagem }}
                    </div>
                </div>
            </div>

            <v-btn
                v-if="mostrarSetas"
                :disabled="!setaDireitaAtiva"
                icon
                class="az-thumbnails-arrow az-thumbnails-arrow--right"
                @click="scrollThumbs('right')"
                aria-label="Ver próximas imagens"
                title="Próximo"
            >
                <v-icon>chevron_right</v-icon>
            </v-btn>
        </div>

        <div id="dots" v-if="ehPaginacaoDots && totalPaginas > 1" class="az-thumbnails-pagination">
            <button
                id="btn-dots"
                v-for="pagina in paginas"
                :key="`pagina-${pagina}`"
                type="button"
                class="az-thumbnails-dot"
                :class="{ 'az-thumbnails-dot--active': pagina === paginaAtual }"
                :aria-label="`Ir para página ${pagina + 1}`"
                @click="irParaPagina(pagina)"
            />
        </div>
    </div>
</template>

<script>

import {convertToUnit} from 'vuetify/lib/util/helpers'

export default {
    name: 'AzThumbnailsBar',
    props: {
        value: {
            type: Number,
            default: undefined
        },
        imagens: {
            type: Array,
            default: () => []
        },
        thumbnailWidth: {
            type: [String, Number],
            default: 80
        },
        thumbnailHeight: {
            type: [String, Number],
            default: 80
        },
        paginationMode: {
            type: String,
            default: 'dots' /** 'dots' | 'scroll' **/
        },
        theme: {
            type: String,
            default: 'dark' /** 'dark' | 'light' **/
        },
        showArrows: {
            type: Boolean,
            default: true
        },
        centralizarImagens: {
            type: Boolean,
            default: true
        },
        showDescription: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            animacaoRodaMouseId: null,
            acumuladorRodaMouse: 0,
            possuiOverflow: false,
            podeRolarEsquerda: false,
            podeRolarDireita: false,
            manipuladorRedimensionamento: null,
            paginaAtual: 0,
            imagensPorPagina: 1
        }
    },
    computed: {
        thumbnailStyle() {
            const width = convertToUnit(this.thumbnailWidth)
            const height = convertToUnit(this.thumbnailHeight)
            return {
                width,
                height
            }
        },
        thumbnailItemStyle() {
            const larguraBase = parseFloat(convertToUnit(this.thumbnailWidth)) || 80
            const larguraMaxima = Math.max(larguraBase, Math.round(larguraBase * 1.45))

            return {
                minWidth: `${larguraBase}px`,
                width: `${larguraBase}px`,
                maxWidth: `${larguraMaxima}px`
            }
        },
        possuiValueSelecionado() {
            return this.value !== null && this.value !== undefined
        },
        ehPaginacaoDots() {
            return this.paginationMode === 'dots'
        },
        mostrarSetas() {
            return this.showArrows && this.totalPaginas > 1
        },
        totalPaginas() {
            if (!this.imagens || !this.imagens.length) return 0
            return Math.ceil(this.imagens.length / this.imagensPorPagina)
        },
        paginas() {
            return Array.from({ length: this.totalPaginas }, (_, i) => i)
        },
        temaClass() {
            return `az-thumbnails-bar--${this.theme}`
        },
        setaEsquerdaAtiva() {
            if (this.ehPaginacaoDots) {
                return this.totalPaginas > 1 && this.paginaAtual > 0
            }
            return this.possuiOverflow && this.podeRolarEsquerda
        },
        setaDireitaAtiva() {
            if (this.ehPaginacaoDots) {
                return this.totalPaginas > 1 && this.paginaAtual < (this.totalPaginas - 1)
            }
            return this.possuiOverflow && this.podeRolarDireita
        }
    },
    watch: {
        imagens() {
            this.$nextTick(() => {
                this.atualizarEstadoImagens()
                this.sincronizarPaginaComValor()
                this.irParaImagemAtiva(false)
            })
        },
        value() {
            this.$nextTick(() => {
                this.sincronizarPaginaComValor()
                this.irParaImagemAtiva()
            })
        }
    },
    mounted() {
        this.manipuladorRedimensionamento = () => {
            this.atualizarEstadoImagens()
            this.sincronizarPaginaComValor()
        }

        window.addEventListener('resize', this.manipuladorRedimensionamento, { passive: true })

        this.$nextTick(() => {
            this.atualizarEstadoImagens()
            this.sincronizarPaginaComValor()
            this.irParaImagemAtiva(false)
        })
    },
    beforeDestroy() {
        if (this.animacaoRodaMouseId) {
            cancelAnimationFrame(this.animacaoRodaMouseId)
            this.animacaoRodaMouseId = null
        }

        if (this.manipuladorRedimensionamento) {
            window.removeEventListener('resize', this.manipuladorRedimensionamento)
            this.manipuladorRedimensionamento = null
        }
    },
    methods: {
        selecionarImagem(indice) {
            this.$emit('input', indice)
            this.$emit('select', this.imagens[indice], indice)
            this.sincronizarPaginaComIndice(indice)

            this.$nextTick(() => {
                this.irParaIndice(indice)
            })
        },

        sincronizarPaginaComIndice(indice) {
            if (!this.ehPaginacaoDots) return
            this.paginaAtual = Math.floor(indice / this.imagensPorPagina)
        },

        sincronizarPaginaComValor() {
            this.sincronizarPaginaComIndice(this.value || 0)
        },

        obterElementosImagem() {
            const elementos = this.$refs.thumbnailItems
            if (!elementos) return []
            return Array.isArray(elementos) ? elementos : [elementos]
        },

        obterLarguraGapImagens() {
            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) return 0

            const estilos = window.getComputedStyle(elementoImagens)
            return parseFloat(estilos.columnGap || estilos.gap || 0) || 0
        },

        obterLarguraRealImagem() {
            const elementos = this.obterElementosImagem()
            if (!elementos.length) {
                return parseFloat(convertToUnit(this.thumbnailWidth)) || 80
            }

            return elementos[0].offsetWidth || (parseFloat(convertToUnit(this.thumbnailWidth)) || 80)
        },

        obterPosicaoImagem(indice) {
            const elementoImagens = this.$refs.thumbStrip
            const elementos = this.obterElementosImagem()

            if (!elementoImagens || !elementos.length || !elementos[indice]) return 0

            const elemento = elementos[indice]
            return Math.max(0, elemento.offsetLeft - elementoImagens.offsetLeft)
        },

        atualizarEstadoImagens() {
            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) {
                this.possuiOverflow = false
                this.podeRolarEsquerda = false
                this.podeRolarDireita = false
                this.imagensPorPagina = 1
                this.paginaAtual = 0
                return
            }

            const existeOverflow = elementoImagens.scrollWidth > elementoImagens.clientWidth + 1
            const maximoScrollEsquerda = elementoImagens.scrollWidth - elementoImagens.clientWidth
            const TOLERANCIA = 2

            this.possuiOverflow = existeOverflow
            this.podeRolarEsquerda = existeOverflow && elementoImagens.scrollLeft > TOLERANCIA
            this.podeRolarDireita = existeOverflow && elementoImagens.scrollLeft < (maximoScrollEsquerda - TOLERANCIA)

            this.atualizarPaginacao()
        },

        atualizarPaginacao() {
            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) {
                this.imagensPorPagina = 1
                this.paginaAtual = 0
                return
            }

            const larguraItem = this.obterLarguraRealImagem()
            const gap = this.obterLarguraGapImagens()
            const estilos = window.getComputedStyle(elementoImagens)
            const paddingEsquerdo = parseFloat(estilos.paddingLeft || 0) || 0
            const paddingDireito = parseFloat(estilos.paddingRight || 0) || 0
            const larguraUtil = Math.max(1, elementoImagens.clientWidth - paddingEsquerdo - paddingDireito)
            const larguraTotalItem = larguraItem + gap

            const quantidade = Math.max(1, Math.floor((larguraUtil + gap) / larguraTotalItem))
            this.imagensPorPagina = quantidade

            this.sincronizarPaginaComValor()
        },

        onThumbScroll() {
            if (!this.ehPaginacaoDots) {
                this.atualizarEstadoImagens()
            }
        },

        irParaPagina(indicePagina) {
            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) return

            const itensAntes = indicePagina * this.imagensPorPagina
            const posicao = this.obterPosicaoImagem(itensAntes)

            elementoImagens.scrollTo({
                left: posicao,
                behavior: 'smooth'
            })

            this.paginaAtual = indicePagina
        },

        irParaIndice(indice, comAnimacao = true) {
            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens || !this.imagens.length) return

            if (this.ehPaginacaoDots) {
                const pagina = Math.floor(indice / this.imagensPorPagina)
                const itensAntes = pagina * this.imagensPorPagina
                const posicao = this.obterPosicaoImagem(itensAntes)

                elementoImagens.scrollTo({
                    left: posicao,
                    behavior: comAnimacao ? 'smooth' : 'auto'
                })

                this.paginaAtual = pagina
                return
            }

            const larguraItem = this.obterLarguraRealImagem()
            const posicaoDesejada = this.obterPosicaoImagem(indice)
            const scrollAtual = elementoImagens.scrollLeft
            const limiteEsquerdo = scrollAtual
            const limiteDireito = scrollAtual + elementoImagens.clientWidth - larguraItem

            if (posicaoDesejada < limiteEsquerdo || posicaoDesejada > limiteDireito) {
                elementoImagens.scrollTo({
                    left: posicaoDesejada,
                    behavior: comAnimacao ? 'smooth' : 'auto'
                })
            }
        },

        irParaImagemAtiva(comAnimacao = true) {
            this.irParaIndice(this.value || 0, comAnimacao)
        },

        scrollThumbs(direction) {
            if (this.ehPaginacaoDots) {
                const proximaPagina = direction === 'left'
                    ? this.paginaAtual - 1
                    : this.paginaAtual + 1

                const paginaLimitada = Math.max(0, Math.min(proximaPagina, this.totalPaginas - 1))
                this.irParaPagina(paginaLimitada)
                return
            }

            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) return

            const passo = Math.max(160, Math.floor(elementoImagens.clientWidth * 0.8))
            const deslocamento = direction === 'left' ? -passo : passo

            elementoImagens.scrollBy({
                left: deslocamento,
                behavior: 'smooth'
            })

            requestAnimationFrame(() => this.atualizarEstadoImagens())
        },

        onThumbWheel(eventoRodaMouse) {
            if (this.ehPaginacaoDots) return

            const elementoImagens = this.$refs.thumbStrip
            if (!elementoImagens) return

            let deslocamentoBruto = Math.abs(eventoRodaMouse.deltaX) > Math.abs(eventoRodaMouse.deltaY)
                ? eventoRodaMouse.deltaX
                : eventoRodaMouse.deltaY

            if (eventoRodaMouse.deltaMode === 1) deslocamentoBruto *= 16

            const deslocamento = deslocamentoBruto * 1.0
            this.acumuladorRodaMouse += deslocamento

            if (this.animacaoRodaMouseId) return

            this.animacaoRodaMouseId = requestAnimationFrame(() => {
                elementoImagens.scrollLeft += this.acumuladorRodaMouse
                this.acumuladorRodaMouse = 0
                this.animacaoRodaMouseId = null
                this.atualizarEstadoImagens()
            })
        }
    }
}
</script>

<style scoped lang="stylus">
    .az-thumbnails-bar
        position: relative
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
        padding: 6px 0
        width: 100%

        &.az-thumbnails-bar--dark
            --az-dot-bg rgba(255,255,255,.28)
            --az-dot-bg-active rgba(255,255,255,.95)
            --az-arrow-bg rgba(255,255,255,.10)
            --az-arrow-bg-disabled rgba(255,255,255,.05)
            --az-arrow-icon-disabled rgba(255,255,255,.35)
            --az-scrollbar-thumb rgba(255,255,255,.35)
            --az-scrollbar-thumb-hover rgba(255,255,255,.55)

        &.az-thumbnails-bar--light
            --az-dot-bg rgba(0,0,0,.20)
            --az-dot-bg-active rgba(0,0,0,.72)
            --az-arrow-bg rgba(0,0,0,.08)
            --az-arrow-bg-disabled rgba(0,0,0,.04)
            --az-arrow-icon-disabled rgba(0,0,0,.28)
            --az-scrollbar-thumb rgba(0,0,0,.24)
            --az-scrollbar-thumb-hover rgba(0,0,0,.40)

    .az-thumbnails-content
        width: 100%
        display: flex
        align-items: center
        justify-content: center

    .az-thumbnails-wrap
        display: flex
        flex-wrap: nowrap
        gap: 10px
        justify-content: flex-start
        align-items: flex-start
        overflow-x: auto
        overflow-y: hidden
        width: 100%
        max-width: 100%
        padding: 6px 6px
        scroll-behavior: smooth
        -webkit-overflow-scrolling: touch
        scrollbar-width: thin
        scrollbar-color: var(--az-scrollbar-thumb) transparent

    .az-thumbnails-wrap--centered
        justify-content: center

    .az-thumbnails-wrap--no-scrollbar
        scrollbar-width: none
        -ms-overflow-style: none

    .az-thumbnails-wrap--no-scrollbar::-webkit-scrollbar
        width: 0
        height: 0
        display: none

    .az-thumbnails-wrap::-webkit-scrollbar
        height: 6px

    .az-thumbnails-wrap::-webkit-scrollbar-track
        background: transparent

    .az-thumbnails-wrap::-webkit-scrollbar-thumb
        background-color: var(--az-scrollbar-thumb)
        border-radius: 999px

    .az-thumbnails-wrap::-webkit-scrollbar-thumb:hover
        background-color: var(--az-scrollbar-thumb-hover)

    .az-thumbnails-arrow
        background: var(--az-arrow-bg) !important
        transition: opacity .2s ease, background-color .2s ease

        &--left
            left: 0

        &--right
            right: 0

        &.v-btn--disabled
            opacity: .45 !important
            background: var(--az-arrow-bg-disabled) !important

        &.v-btn--disabled .v-icon
            color: var(--az-arrow-icon-disabled) !important

    .az-thumbnail
        cursor: pointer
        border: 1px solid #e0e0e0
        border-radius: 4px
        overflow: hidden
        transition: border-color 0.2s ease-in-out
        display: flex
        flex-direction: column
        align-items: center
        justify-content: flex-start
        flex: 0 0 auto

        &--active
            border-color: var(--v-primary-base, #1976D2)

    .az-thumbnail-image-box
        flex: 0 0 auto

    .az-thumbnail-img
        width: 100%
        height: 100%
        object-fit: cover
        display: block

    .az-thumbnail-description
        margin-top: 6px
        width: 100%
        padding: 0 6px 6px
        text-align: center
        font-size: 12px
        font-weight: bold
        line-height: 1.25
        white-space: normal
        word-break: break-word
        overflow-wrap: anywhere

    .az-thumbnails-pagination
        display: flex
        align-items: center
        justify-content: center
        gap: 8px
        margin-top: 10px

    .az-thumbnails-dot
        width: 10px
        height: 10px
        border-radius: 999px
        border: none
        padding: 0
        cursor: pointer
        background: var(--az-dot-bg)
        transition: transform .2s ease, background-color .2s ease, opacity .2s ease

        &:hover
            transform: scale(1.12)

        &--active
            background: var(--az-dot-bg-active)
            transform: scale(1.2)

    @media (max-width: 600px)
        .az-thumbnails-wrap
            gap: 8px

        .az-thumbnail-description
            font-size: 11px
            line-height: 1.2
            padding: 0 4px 4px
</style>
